import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { antdDarkTheme, antdTheme } from "@wms/ui";
import { App as AntApp, ConfigProvider, theme as antTheme } from "antd";
import ruRU from "antd/locale/ru_RU";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { ApiError } from "./api/client";
import { useAuthStore } from "./auth/store";
import "./styles.css";
import { useUiStore } from "./ui/theme";
import { PersonalLookSync, primeLook } from "./workspace/PersonalLook";
import { applyLookVariables, themeWithLook, useLookStore } from "./workspace/look";

dayjs.locale("ru");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // a refusal (403), a missing record (404) or a validation error will not change on a retry
      retry: (count, error) => count < 1 && !(error instanceof ApiError && (error.problem.status ?? 500) < 500),
      staleTime: 10_000,
      refetchOnWindowFocus: false,
    },
  },
});

function Root() {
  const mode = useUiStore((s) => s.theme);
  const density = useUiStore((s) => s.density);
  const setDensity = useUiStore((s) => s.setDensity);
  const savedDensity = useAuthStore((s) => s.me?.preferences?.density);
  React.useEffect(() => {
    if (savedDensity === "large" || savedDensity === "default") setDensity(savedDensity);
  }, [savedDensity, setDensity]);
  // ТЗ «Рабочие столы» 31.10.2026 (UXW-10, CW-09): the own look of the cabinet in the address — null with the option
  // off, signed out or in the dark theme, and then everything below is exactly as before
  const look = useLookStore((s) => s.active);
  React.useEffect(() => {
    document.documentElement.dataset.theme = mode;
    document.documentElement.dataset.density = density;
    document.body.style.background = mode === "dark" ? "#141518" : look?.tokens.canvas ?? "#f5f6f8";
    applyLookVariables(document.documentElement, look);
  }, [mode, density, look]);
  const base = mode === "dark" ? { ...antdDarkTheme, algorithm: antTheme.darkAlgorithm } : antdTheme;
  // ТЗ 1.2 раздел 4.2: «Крупный интерфейс» — 18 px text and 44 px controls as a setting, the default stays as it was
  const sized = density === "large" ? { ...base, token: { ...base.token, fontSize: 18, controlHeight: 44, controlHeightSM: 36, controlHeightLG: 52 } } : base;
  const themeConfig = themeWithLook(sized, look);
  return (
    <ConfigProvider locale={ruRU} theme={themeConfig}>
      <AntApp>
        <BrowserRouter>
          <PersonalLookSync />
          <App />
        </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
}

// UXW-17: the first paint already has the look of the signed-in principal of this cabinet (or its standard one)
primeLook();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Root />
    </QueryClientProvider>
  </React.StrictMode>,
);
